// Backend · Alerts: the Channels tab — the webhook and email registry that
// rules deliver through.

import type { NotificationChannel } from '@/api/types';
import { Badge } from './backend-alerts-parts';

export interface ChannelsTabProps {
  channels: readonly NotificationChannel[];
  canEdit: boolean;
  onEdit: (channel: NotificationChannel) => void;
}

// ── Channels ─────────────────────────────────────────────────────────────────
/** The accessible name of the channel list, which scrolls on its own. */
export const ALERT_CHANNEL_LIST_LABEL = 'Notification channels';

export function ChannelsTab({ channels, canEdit, onEdit }: ChannelsTabProps) {
  return (
    // Like the rules list: it scrolls on its own and its rows take no focus,
    // so the list itself is a focusable, named region (axe
    // scrollable-region-focusable). An editor reaches each channel by keyboard
    // through its row's Edit button; a click on the row opens it too.
    <div className="alert-channel-list" role="region" aria-label={ALERT_CHANNEL_LIST_LABEL} tabIndex={0} style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' }}>
      {channels.length === 0 ? (
        <div className="muted" style={{ padding: '40px 12px', fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}>
          No channels. Add a Slack/Discord webhook or an email list, then attach it to rules.
        </div>
      ) : channels.map((c) => (
        <div key={c.id} className="panel" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: canEdit ? 'pointer' : 'default' }}
          onClick={() => canEdit && onEdit(c)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
            <div className="muted mono" style={{ fontSize: 11 }}>
              {c.kind === 'email' ? (c.recipients?.join(', ') || 'no recipients') : (c.configured ? 'webhook configured ✓' : 'no url')}
            </div>
          </div>
          <Badge color="var(--ink-3)">{c.kind}</Badge>
          {canEdit && (
            <button
              type="button"
              className="btn alert-channel-edit"
              aria-label={`Edit channel ${c.name}`}
              onClick={(event) => { event.stopPropagation(); onEdit(c); }}
            >
              Edit
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
